import {
  IsDateString,
  IsEnum,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsOptional,
  IsString,
  Max,
  MaxLength,
  Min,
  ValidateIf,
} from 'class-validator';

/**
 * Lifecycle of an NSP access request. A request starts life as `pending` when a
 * clinician is blocked by the NSP audit gate, and a support manager moves it to
 * `approved` (granting a time-limited bypass) or `rejected`. An `approved`
 * request whose bypass window has elapsed is retired to `expired` the next time
 * the same clinician is blocked for that patient, and a fresh `pending` request
 * is opened for review.
 */
export enum NspAccessRequestStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  Expired = 'expired',
}

/**
 * Why the NSP audit gate denied access. Everything except `consent_denied` is a
 * potential false negative (data/config lag) and is therefore bypassable by
 * support. `consent_denied` reflects the patient actively withholding access
 * (spærring / manglende samtykke) and is NEVER bypassable — see
 * {@link isNspDenialReasonBypassable}.
 */
export enum NspAccessDenialReason {
  /** No usable user identity (CPR/JWT) resolved for the acting clinician. */
  MissingUserInfo = 'missing_user_info',
  /** Clinician has no healthcare authorization code / on-behalf-of selection. */
  MissingAuthorization = 'missing_authorization',
  /** Patient consent not given — legally binding, never bypassable. */
  ConsentDenied = 'consent_denied',
  /** No sufficient treatment relationship (behandlingsrelation / BRS). */
  TreatmentRelationshipMissing = 'treatment_relationship_missing',
  /** The NSP audit itself errored (technical/config failure). */
  AuditError = 'audit_error',
}

/**
 * A genuine consent denial must never be overridable by support. Every other
 * denial reason represents a plausible false negative that a manager may choose
 * to bypass after review.
 */
export function isNspDenialReasonBypassable(
  reason: NspAccessDenialReason,
): boolean {
  switch (reason) {
    case NspAccessDenialReason.MissingUserInfo:
    case NspAccessDenialReason.MissingAuthorization:
    case NspAccessDenialReason.TreatmentRelationshipMissing:
    case NspAccessDenialReason.AuditError:
      return true;
    case NspAccessDenialReason.ConsentDenied:
      return false;
    default: {
      // Exhaustive by design: a new denial reason must not silently inherit
      // "bypassable" — this fails to compile until its bypassability is decided.
      const unhandled: never = reason;
      void unhandled;
      // Fail closed at runtime for an unexpected value (e.g. a legacy row).
      return false;
    }
  }
}

/** Ceiling on how many requests one admin search may pull at a time. */
export const NSP_ACCESS_REQUEST_MAX_PAGE_SIZE = 100;

export class NspAccessRequestAdminSearchQueryDto {
  @IsInt()
  @Min(0)
  skip: number;

  @IsInt()
  @Min(1)
  @Max(NSP_ACCESS_REQUEST_MAX_PAGE_SIZE)
  take: number;

  /** Matches clinician name/email, medical unit name, or health record id. */
  @IsOptional()
  @IsString()
  @MaxLength(200)
  searchText?: string;

  /** Omitted = all statuses. */
  @IsOptional()
  @IsEnum(NspAccessRequestStatus)
  status?: NspAccessRequestStatus;
}

/**
 * Ceiling on the bypass window, shared by the admin UI (input max) and the API
 * (enforced in review) so a direct API call cannot grant an effectively
 * unbounded bypass of the national consent check.
 */
export const NSP_BYPASS_MAX_DAYS = 365;

/**
 * Update a request's review outcome. `status` must be `approved` or `rejected`
 * (a request cannot be moved back to `pending` or `expired`). `bypassExpiresAt`
 * is required when approving — support-configurable, the admin UI defaults to one
 * week out — and must be a future timestamp no more than
 * {@link NSP_BYPASS_MAX_DAYS} days out; it is cleared when rejecting.
 * `reviewNote` is required when approving: it is the audit trail for overriding a
 * national healthcare access check. The window bounds and the remaining
 * transition rules are enforced server-side in the review service.
 */
export class UpdateNspAccessRequestDto {
  @IsIn([NspAccessRequestStatus.Approved, NspAccessRequestStatus.Rejected])
  status: NspAccessRequestStatus;

  /** Required when approving; ignored (and cleared) when rejecting. */
  @ValidateIf(
    (dto: UpdateNspAccessRequestDto) =>
      dto.status === NspAccessRequestStatus.Approved ||
      dto.bypassExpiresAt != null,
  )
  @IsNotEmpty()
  @IsDateString()
  bypassExpiresAt?: string;

  /** Required when approving — the audit trail for granting the bypass. */
  @ValidateIf(
    (dto: UpdateNspAccessRequestDto) =>
      dto.status === NspAccessRequestStatus.Approved || dto.reviewNote != null,
  )
  @IsString()
  @IsNotEmpty()
  @MaxLength(2000)
  reviewNote?: string;
}

/**
 * Diagnostic detail captured from the NSP audit at denial time. One JSON blob
 * rather than a column each, so a further NSP diagnostic can be recorded without
 * a migration. Every field is optional: a denial raised before NSP was contacted
 * records none of them.
 */
export interface NspAccessRequestMetadata {
  /**
   * Relation codes BRS reported, sufficient or not — absent when the check never
   * answered, empty when it answered with no relation at all.
   */
  brsRelations?: string;
  /** The same relations, each with the national registry it was found in. */
  brsRelationsBySource?: string;
}

/**
 * NSP access request as shown in the admin app. Flat projection. Patient
 * identifiers (SSN/CPR, name) are intentionally omitted per PHI rules — only
 * the (non-PHI) health record id is exposed so support can correlate the case.
 */
export interface NspAccessRequestAdminDto {
  id: string;
  created: string | Date;
  status: NspAccessRequestStatus;
  denialReason: NspAccessDenialReason;
  /** Whether this reason is eligible for a bypass at all (consent is not). */
  bypassable: boolean;
  /** Acting clinician's healthcare authorization code (autorisationskode). */
  authorizationCode?: string;
  /** Medical unit's SOR provider id (ydernummer). */
  sorProviderId?: string;
  /** Diagnostic detail captured from the NSP audit at denial time. */
  metadata?: NspAccessRequestMetadata;
  /** Whether the clinician was acting on behalf of another doctor. */
  onBehalfOf: boolean;
  /** The on-behalf-of doctor's authorization code, when applicable. */
  onBehalfOfAuthorizationCode?: string;
  /** The on-behalf-of doctor's Dermloop user id, when applicable. */
  onBehalfOfUserId?: string;
  /** How many times this clinician has been blocked for this patient. */
  attemptCount: number;
  lastAttemptAt: string | Date;
  bypassExpiresAt?: string | Date;
  userId?: string;
  userName?: string;
  userEmail?: string;
  healthRecordId?: string;
  medicalUnitId?: string;
  medicalUnitName?: string;
  jurisdictionalGroupId?: string;
  jurisdictionalGroupName?: string;
  federationId?: string;
  federationName?: string;
  reviewedByUserId?: string;
  reviewedByName?: string;
  reviewedAt?: string | Date;
  reviewNote?: string;
}
